import React, { useState } from "react";
import { api } from "../lib/api";
import { Surface, Btn, Field, Input } from "./Primitives";
import { Sparkle, Upload, Check, Warning } from "@phosphor-icons/react";

const KINDS = [
  { id: "insurance",    label: "Insurance certificate",
    desc: "Pulls provider, policy number, expiry, coverage, special conditions." },
  { id: "registration", label: "Registration certificate",
    desc: "Pulls registration mark, manufacturer/model, serial, year, TC ref." },
  { id: "arc",          label: "ARC certificate",
    desc: "Pulls ARC expiry, CAMO provider, ARC issued date + reference." },
];

const SC_FIELDS_FOR_INSURANCE = ["special_conditions"];

export default function DocAIUploadModal({ aircraftId, onClose, onApplied }) {
  const [step, setStep] = useState("pick");   // pick | upload | review | applied
  const [kind, setKind] = useState("insurance");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [extraction, setExtraction] = useState(null);
  const [selected, setSelected] = useState({});
  const [specialConditions, setSpecialConditions] = useState([]);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post(`/aircraft/${aircraftId}/doc-extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setExtraction(data);
      // Pre-select every field that the backend would project on the aircraft
      const preselect = {};
      Object.keys(data.suggested_patch || {}).forEach((k) => { preselect[k] = true; });
      setSelected(preselect);
      // Pre-populate special_conditions from the AI output (insurance only)
      if (kind === "insurance" && Array.isArray(data.fields?.special_conditions)) {
        setSpecialConditions(data.fields.special_conditions);
      }
      setStep("review");
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!extraction) return;
    setBusy(true); setError("");
    try {
      const fields = {};
      Object.entries(selected).forEach(([k, v]) => {
        if (v) fields[k] = extraction.suggested_patch[k];
      });
      const body = { fields };
      if (kind === "insurance") body.special_conditions = specialConditions;
      await api.post(`/doc-extractions/${extraction.id}/apply`, body);
      setStep("applied");
      onApplied?.();
    } catch (e) {
      setError(e?.response?.data?.detail || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="doc-ai-modal">
      <Surface className="w-full max-w-3xl p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-medium flex items-center gap-2">
            <Sparkle size={20} className="text-amber" />AI document extraction
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 text-2xl leading-none">×</button>
        </div>

        {error && <div className="p-3 border border-red-500/40 bg-red-500/5 text-sm text-red-300 mb-4" data-testid="doc-ai-error">{error}</div>}

        {step === "pick" && (
          <>
            <div className="text-xs text-zinc-500 mb-4">Pick a document type. The AI will extract the relevant fields and let you review before saving.</div>
            <div className="grid grid-cols-1 gap-3" data-testid="doc-ai-kinds">
              {KINDS.map((k) => (
                <button key={k.id} onClick={() => { setKind(k.id); setStep("upload"); }} className={`text-left p-4 border ${kind === k.id ? "border-amber" : "border-zinc-800"} hover:border-amber transition`} data-testid={`doc-ai-kind-${k.id}`}>
                  <div className="text-zinc-200 font-medium">{k.label}</div>
                  <div className="text-xs text-zinc-500 mt-1">{k.desc}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "upload" && (
          <>
            <div className="text-xs text-zinc-500 mb-3">Upload the {kind} document (PDF or image, ≤20 MB).</div>
            <div className="border border-dashed border-zinc-800 p-8 text-center" data-testid="doc-ai-dropzone">
              <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" id="doc-ai-file" />
              <label htmlFor="doc-ai-file" className="cursor-pointer">
                <Upload size={32} className="mx-auto text-zinc-500 mb-3" />
                <div className="text-sm text-zinc-300">{file ? file.name : "Click to choose a file"}</div>
                <div className="text-[10px] text-zinc-600 font-mono mt-2 uppercase tracking-wider">PDF / JPG / PNG</div>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setStep("pick")}>Back</Btn>
              <Btn onClick={upload} disabled={busy || !file} data-testid="doc-ai-upload-btn">{busy ? "Extracting (may take 20s)…" : "Extract with AI"}</Btn>
            </div>
          </>
        )}

        {step === "review" && extraction && (
          <>
            <div className="text-xs text-zinc-500 mb-4">Review what the AI extracted. Uncheck anything you don&apos;t want to apply. Model: <span className="font-mono">{extraction.model}</span></div>
            {Object.keys(extraction.suggested_patch || {}).length === 0 && (
              <div className="p-4 border border-amber/40 bg-amber/5 text-sm text-zinc-300">
                <Warning size={16} className="inline mr-2 text-amber" />The AI didn&apos;t find any fields it could map onto the aircraft. Try a clearer scan or a different document type.
              </div>
            )}
            <div className="space-y-2" data-testid="doc-ai-review-fields">
              {Object.entries(extraction.suggested_patch || {}).map(([k, v]) => (
                <label key={k} className="flex items-start gap-3 p-3 border border-zinc-800 hover:border-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={!!selected[k]} onChange={(e) => setSelected({ ...selected, [k]: e.target.checked })} className="mt-1" data-testid={`doc-ai-field-${k}`} />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{k.replace(/_/g, " ")}</div>
                    <div className="text-sm text-zinc-200 mt-1 font-mono break-all">{String(v)}</div>
                  </div>
                </label>
              ))}
            </div>

            {kind === "insurance" && SC_FIELDS_FOR_INSURANCE.length > 0 && (
              <div className="mt-5 pt-5 border-t border-zinc-800">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono mb-2">Special conditions extracted</div>
                <div className="text-xs text-zinc-500 mb-3">Things the policy requires the owner to be aware of. Edit or remove items before applying.</div>
                <div className="space-y-2" data-testid="doc-ai-special-conditions">
                  {specialConditions.map((sc, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <Input value={sc} onChange={(e) => {
                        const next = [...specialConditions]; next[i] = e.target.value; setSpecialConditions(next);
                      }} data-testid={`doc-ai-sc-${i}`} />
                      <button onClick={() => setSpecialConditions(specialConditions.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-400 self-center px-2" data-testid={`doc-ai-sc-remove-${i}`}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setSpecialConditions([...specialConditions, ""])} className="text-amber text-[10px] uppercase tracking-[0.2em] font-mono hover:underline" data-testid="doc-ai-sc-add">+ Add condition</button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setStep("upload")}>Back</Btn>
              <Btn onClick={apply} disabled={busy} data-testid="doc-ai-apply-btn">{busy ? "Applying…" : "Apply to aircraft"}</Btn>
            </div>
          </>
        )}

        {step === "applied" && (
          <div className="text-center py-12" data-testid="doc-ai-applied">
            <Check size={40} className="mx-auto text-emerald-400 mb-4" />
            <div className="text-zinc-200 text-lg">Saved to aircraft profile</div>
            <div className="text-sm text-zinc-500 mt-2">You can review the changes on the Profile tab.</div>
            <Btn className="mt-6" onClick={onClose} data-testid="doc-ai-close-btn">Done</Btn>
          </div>
        )}
      </Surface>
    </div>
  );
}
